import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CONTACT_INFO, SOCIAL_LINKS } from "@/data/content";
import { SERVICE_PAGES } from "@/data/serviceDetails";
import { useTheme } from "@/context/ThemeContext";

export default function Footer() {
  const { theme } = useTheme();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.newsletter({ email });
      toast.success(res.data.message);
      setEmail("");
    } catch {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="border-t border-border/50 bg-card" data-testid="main-footer">
      <div className="mx-auto max-w-7xl px-6 md:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex items-center">
              <div className={`w-fit ${theme === "dark" ? "rounded-lg bg-white px-3 py-1.5" : ""}`}>
                <img src="/assets/logo-240.webp" alt="Serpens Technology logo" width="116" height="32" loading="lazy" decoding="async" className="h-8 w-auto" />
              </div>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-muted-foreground max-w-sm">
              Building digital solutions that scale businesses. Your global partner for custom software, AI, mobile apps, websites, and cloud — from Bengaluru to the world.
            </p>
            <div className="mt-6 space-y-2.5 text-sm text-muted-foreground">
              <a href={`mailto:${CONTACT_INFO.email}`} data-testid="footer-email" className="flex items-center gap-2.5 hover:text-foreground transition-colors">
                <Mail className="h-4 w-4 text-accent" /> {CONTACT_INFO.email}
              </a>
              <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`} data-testid="footer-phone" className="flex items-center gap-2.5 hover:text-foreground transition-colors">
                <Phone className="h-4 w-4 text-accent" /> {CONTACT_INFO.phone}
              </a>
              <p className="flex items-center gap-2.5">
                <MapPin className="h-4 w-4 text-accent" /> {CONTACT_INFO.address}
              </p>
            </div>
            {SOCIAL_LINKS.length > 0 && (
              <div className="mt-6 flex gap-3" data-testid="footer-social-links">
                {SOCIAL_LINKS.map((s) => (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    data-testid={`footer-social-${s.name.toLowerCase()}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-secondary/25 text-muted-foreground transition-all duration-300 hover:border-accent hover:text-accent hover:-translate-y-0.5"
                  >
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <p className="font-display text-sm font-semibold uppercase tracking-widest text-foreground">Services</p>
            <ul className="mt-5 space-y-2.5">
              {SERVICE_PAGES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-sm text-muted-foreground hover:text-accent transition-colors">
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <p className="font-display text-sm font-semibold uppercase tracking-widest text-foreground">Company</p>
            <ul className="mt-5 space-y-2.5">
              {[["About Us", "/about"], ["Portfolio", "/portfolio"], ["Blog", "/blog"], ["Careers", "/careers"], ["Contact", "/contact"], ["Privacy Policy", "/privacy-policy"], ["Terms of Service", "/terms"]].map(([label, path]) => (
                <li key={label}>
                  <Link to={path} className="text-sm text-muted-foreground hover:text-accent transition-colors">{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="font-display text-sm font-semibold uppercase tracking-widest text-foreground">Stay Ahead</p>
            <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
              Monthly insights on software, AI, and digital transformation. No spam, ever.
            </p>
            <form onSubmit={subscribe} className="mt-5 flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your work email"
                data-testid="newsletter-email-input"
                className="h-11 flex-1 rounded-full border border-border bg-background px-4 text-sm outline-none transition-shadow focus:ring-2 focus:ring-accent/50"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-button"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary text-white shadow-lg shadow-blue-800/25 transition-all duration-300 hover:shadow-blue-800/40 active:scale-95 disabled:opacity-60"
                aria-label="Subscribe"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
            <p className="mt-4 text-xs text-muted-foreground/70">Join 2,000+ technology leaders reading our insights.</p>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-border/50 pt-8 sm:flex-row">
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Serpens Technology Pvt. Ltd. All rights reserved.</p>
          <p className="text-xs text-muted-foreground">Building Digital Solutions That Scale Businesses.</p>
        </div>
      </div>
    </footer>
  );
}
